/**
 * THE LEAGUE'S EMAILS — written once here, in the director's voice, and rendered two ways.
 *
 * ⚠ THESE STARTED AS COPY-AND-PASTE TEXT (2026-08-25). The platform has sent them itself since
 * 2026-09-07, but nothing filled the blanks, so players received "Hi [ NAME ]," and "starting
 * [ START TIME ]" exactly as written (found 2026-10-05). Now:
 *
 *   - `buildEmail(kind, facts)` says WHAT the email is — a heading, her paragraphs word for word, a
 *     facts box, bullets, standings, and two slots: her optional note, and the "set up your
 *     account" button for a player who has never set a password.
 *   - `emailText(doc, mode)` renders plain text. In `copy` mode (the Emails tab's Copy, for pasting
 *     into her own mail) whatever only she can supply is a loud bracket, as before. In `send` mode
 *     nothing is ever bracketed: a missing fact is left out rather than shown as a hole.
 *   - The API renders the same document as branded HTML (`apps/api/src/mail/league-email.ts`).
 *
 * Pure, like the other engines here: facts in, document out, no I/O.
 */

/**
 * THE OWNER'S NAMES, adopted 2026-09-07, and they are the database's names too.
 *
 * These were `confirmation | welcome | standings | celebration` and the `message_template_type`
 * column said `welcome | kickoff | results | final_results` for the same four things. Two
 * vocabularies for one set of messages is a translation layer somebody has to keep in step, and the
 * moment the platform started SENDING these rather than printing them for copying, that translation
 * would have sat between a director pressing send and the row recording what she sent.
 *
 * `survey` has no counterpart in the column and does not need one: the post-league survey link is
 * hers to distribute, because the answers are anonymous and a platform-sent link is a platform that
 * knows who was asked.
 */
export type TemplateKind =
  | 'welcome'
  | 'kickoff'
  | 'reminder'
  | 'results'
  | 'final_results'
  | 'survey';

export interface TemplateMeta {
  kind: TemplateKind;
  title: string;
  /** When she would send it, so the list reads as a calendar rather than a menu. */
  when: string;
}

export const TEMPLATES: TemplateMeta[] = [
  { kind: 'welcome', title: 'Registration welcome', when: 'When a player registers, or when you invite them' },
  { kind: 'kickoff', title: 'Kickoff', when: '9 AM, four days before the first session' },
  { kind: 'reminder', title: 'Session reminder', when: '9 AM, four days before each later session' },
  { kind: 'results', title: 'Session celebration', when: 'The morning after each session, once scores are closed' },
  { kind: 'final_results', title: 'Final celebration', when: 'The morning after the last session, once scores are closed' },
  { kind: 'survey', title: 'How did we do?', when: 'A day or two after you close the league' },
];

export interface SessionFact {
  number: number;
  /** "Monday, October 12", in the market's zone. Pre-formatted by the caller. */
  date: string;
  /** "October 12" — for a list of dates that already says which weekday. */
  shortDate?: string;
  /** "6:30 PM MDT" — the start time in the market's zone, named. */
  time?: string | null;
  /** True once the session has been played. */
  played: boolean;
}

export interface StandingLine {
  rank: number;
  name: string;
  points: number;
  tied: boolean;
}

export interface TemplateFacts {
  brand: string;
  leagueName: string;
  city: string;
  venueName: string | null;
  /** "123 Main Street, Castle Rock, CO 80104" — null when the venue has no address on file. */
  venueAddress?: string | null;
  /** The NMJL card the league plays — "Your 2026 NMJL card". */
  cardYear?: number | null;
  directorFirstName: string;
  /** The director's full name — "League director:" in the facts, and the sign-off (Courtney, 2026-10-12). */
  directorName?: string;
  /** The session this email is about: the one reminded of, or the one celebrated. */
  aboutSession?: number | null;
  /** The league's last session — its reminder and celebration have their own words. */
  lastSession?: number;
  /** Whether any session's scores are closed, so leaders may be named (Courtney, 2026-10-12). */
  resultsApproved?: boolean;
  /** The player's own standings page — the link in reminders and celebrations. */
  standingsUrl?: string | null;
  playerUrl?: string | null;
  /** Where to find the next league — the final celebration's last line. */
  registrationUrl?: string | null;
  /** The league's overall winners on 25 or more, as "Sarah K.". */
  overallWinners?: string[];
  weekday: string;
  sessions: SessionFact[];
  /** Ordered. Empty until a session has been scored. */
  standings: StandingLine[];
  /** Names, for the session just played. */
  lastSessionWinners: string[];

  /**
   * The public link to this league's survey. Null until the league is closed, because the survey
   * does not exist before then — and a template that renders a dead link is worse than one that
   * says the survey is not open yet.
   */
  surveyUrl?: string | null;
}

/** A piece of text that is either known, or a blank only the director can fill. */
export type Text = string | { blank: string };

export type EmailBlock =
  | { kind: 'paragraph'; text: Text[] }
  | { kind: 'facts'; rows: { label: string; lines: Text[] }[] }
  | { kind: 'bullets'; items: string[] }
  | { kind: 'standings'; title: string; rows: StandingLine[]; empty: string }
  /** Her optional note. In copy mode, `fallback` (if any) shows as a blank in its place. */
  | { kind: 'note'; fallback?: string }
  /** "Set up your account", for a player who has never set a password. Nothing otherwise. */
  | { kind: 'setup' }
  /** "See the latest standings: <link>" — a real link in the HTML, the address in plain text. */
  | { kind: 'link'; label: string; url: string };

export interface EmailDocument {
  subject: string;
  /** Whose email this is — for the footer ("on the roster for …"). */
  league: string;
  /** The big line in the branded header. */
  heading: string;
  /** Under it: the league, and where in the league this email sits. */
  subheading: string;
  /** Named: "Hi Barb,". Otherwise the group greeting she wrote ("Hi everyone,"). */
  greeting: { named: true } | { named: false; text: string };
  blocks: EmailBlock[];
  signOff: [string, string];
  /**
   * HER OWN WORDS, when the director edited this email (Courtney, 2026-10-12) — greeting to
   * sign-off, replacing everything the blocks would say. "[First Name]" is filled per player.
   */
  body?: string;
}

/** The token a director's edited email uses for the reader's first name. */
export const FIRST_NAME = '[First Name]';

/**
 * WHAT THE DIRECTOR EDITS: the email as it reads, with the reader's name left as a token. The same
 * text she would see in her inbox, so editing it is editing the email, not a template language.
 */
export function editableText(doc: EmailDocument): string {
  if (doc.body !== undefined) return doc.body;
  return emailText(doc, 'send', { firstName: FIRST_NAME });
}

export interface RenderedTemplate {
  subject: string;
  body: string;
}

/** What only the moment of sending knows. */
export interface Personal {
  firstName?: string | null;
  note?: string | null;
  /** Where "Set up your account" goes. Absent: she already signs in, and there is no button. */
  setupUrl?: string | null;
  /** Her own "Stop league emails" link (2026-10-12). Absent on the director's copy. */
  unsubscribeUrl?: string | null;
}

/** A blank the director fills in. Deliberately loud, so it cannot be pasted by accident. */
const BLANK = (what: string) => `[ ${what} ]`;
const blank = (what: string): Text => ({ blank: what });

/**
 * Her own words. Every template ends with one rather than a signature we made up for her.
 *
 * "Tuesdays" becomes "Tuesday" — she is signing off before ONE session, not the whole run.
 * Capitalisation is preserved: lowercasing the plural to strip its "s" also produced "See you
 * tuesday", which reads as a typo in an email a hundred people receive.
 */
function signOff(facts: TemplateFacts): [string, string] {
  const session = facts.weekday.replace(/s$/, '');
  return [`See you ${session || BLANK('SESSION')},`, facts.directorFirstName];
}


/** "October 12, October 19 and October 26". */
function dateList(sessions: SessionFact[]): string {
  const dates = sessions.map((s) => s.shortDate ?? s.date);
  return dates.length <= 1 ? dates.join('') : `${dates.slice(0, -1).join(', ')} and ${dates[dates.length - 1]}`;
}

export function buildEmail(kind: TemplateKind, facts: TemplateFacts): EmailDocument {
  switch (kind) {
    case 'welcome':
      return confirmation(facts);
    case 'kickoff':
      return welcome(facts);
    case 'reminder':
      return reminder(facts);
    case 'results':
      return standings(facts);
    case 'final_results':
      return celebration(facts);
    case 'survey':
      return survey(facts);
  }
}

/** Plain text, as pasted: the Emails tab's Read and Copy. Blanks only she can fill stay loud. */
export function renderTemplate(kind: TemplateKind, facts: TemplateFacts): RenderedTemplate {
  const doc = buildEmail(kind, facts);
  return { subject: doc.subject, body: emailText(doc, 'copy') };
}

/** One piece of text: in `send` mode a blank is left out, never shown as a hole. */
export function textOf(parts: Text[], mode: 'copy' | 'send'): string {
  return parts.map((p) => (typeof p === 'string' ? p : mode === 'copy' ? BLANK(p.blank) : '')).join('');
}

/** "Hi Barb," — or, sending to somebody whose first name is missing, "Hi there,". */
export function greetingOf(doc: EmailDocument, mode: 'copy' | 'send', personal: Personal = {}): string {
  if (!doc.greeting.named) return doc.greeting.text;
  const name = personal.firstName?.trim();
  return `Hi ${name || (mode === 'copy' ? BLANK('NAME') : 'there')}!`;
}

export function emailText(doc: EmailDocument, mode: 'copy' | 'send', personal: Personal = {}): string {
  if (doc.body !== undefined) {
    const name = personal.firstName?.trim() || (mode === 'copy' ? FIRST_NAME : 'there');
    const text = doc.body.split(FIRST_NAME).join(name);
    return personal.setupUrl
      ? `${text}\n\nSet up your account to see your table, who you are playing and where you stand:\n  ${personal.setupUrl}`
      : text;
  }
  const out: string[] = [greetingOf(doc, mode, personal), ''];
  for (const block of doc.blocks) {
    switch (block.kind) {
      case 'paragraph': {
        const text = textOf(block.text, mode).trim();
        if (text) out.push(text, '');
        break;
      }
      case 'facts':
        for (const row of block.rows) {
          const lines = row.lines.map((line) => textOf([line], mode)).filter(Boolean);
          if (lines.length === 0) continue;
          out.push(row.label.toUpperCase(), ...lines.map((line) => `  ${line}`), '');
        }
        break;
      case 'bullets':
        for (const item of block.items) out.push(`  ${item}`, '');
        break;
      case 'standings': {
        out.push(block.title.toUpperCase());
        if (block.rows.length === 0) {
          if (mode === 'copy') out.push(BLANK(block.empty));
        } else {
          /** Padded so the columns line up in a monospace client and read fine in a proportional one. */
          const width = Math.max(...block.rows.map((row) => row.name.length));
          for (const row of block.rows) {
            const rank = `${row.rank}`.padEnd(4);
            out.push(`  ${rank}${row.name.padEnd(width + 2)}${row.points}`);
          }
        }
        out.push('');
        break;
      }
      case 'note': {
        const note = personal.note?.trim();
        if (note) out.push(note, '');
        else if (mode === 'copy' && block.fallback) out.push(BLANK(block.fallback), '');
        break;
      }
      case 'setup':
        if (personal.setupUrl) {
          out.push('Set up your account to see your table, who you are playing and where you stand:', `  ${personal.setupUrl}`, '');
        }
        break;
      case 'link':
        out.push(`${block.label} ${block.url}`, '');
        break;
    }
  }
  out.push(...doc.signOff.filter(Boolean));
  return out.join('\n');
}

/**
 * THE SURVEY ASK, and the reason it stays copy-only: a request for honest feedback that arrives from
 * a platform is a form, and one that arrives from the person who ran your league is a question.
 *
 * SO IT SAYS ANONYMOUS TWICE, in her voice. That is the only thing standing between this and a
 * politeness survey, and a director asking on her own behalf is exactly the person who has to
 * promise it.
 */
function survey(facts: TemplateFacts): EmailDocument {
  const link: Text = facts.surveyUrl ?? blank('SURVEY LINK — close the league to create it');
  return {
    subject: `How did ${facts.leagueName} go?`,
    heading: 'How did we do?',
    league: facts.leagueName,
    subheading: facts.leagueName,
    greeting: { named: false, text: 'Hi everyone,' },
    blocks: [
      { kind: 'paragraph', text: [`Now that ${facts.leagueName} has finished, I would really like to know what you thought.`] },
      { kind: 'paragraph', text: ['It is six quick questions and one box for anything else. It should take two minutes, and you can skip anything you would rather not answer.'] },
      { kind: 'paragraph', text: [link] },
      { kind: 'paragraph', text: ['It is completely anonymous — I see the answers all together and I cannot tell who wrote what. So please be honest. If something annoyed you, that is the useful bit.'] },
      { kind: 'paragraph', text: ['Thank you, genuinely. It is how the next one gets better.'] },
      { kind: 'note' },
    ],
    signOff: signOff(facts),
  };
}

/* ------------------------------------------------------------------------------------------------
 * COURTNEY'S WORDING — her "Communication Guidance", 2026-10-11, as she answered it on
 * 2026-10-12: leaders are the top five on 25 or more, names are "Sarah K.", the standings link opens
 * the player's own login, and every email signs off with the director's name.
 * ---------------------------------------------------------------------------------------------- */

const directorOf = (facts: TemplateFacts) => facts.directorName?.trim() || facts.directorFirstName;
const close = (facts: TemplateFacts, line: string): [string, string] => [line, directorOf(facts)];
const about = (facts: TemplateFacts): SessionFact | undefined =>
  facts.sessions.find((s) => s.number === facts.aboutSession) ?? facts.sessions.find((s) => !s.played);
const after = (facts: TemplateFacts, session: SessionFact | undefined) =>
  session ? facts.sessions.find((s) => s.number === session.number + 1) : undefined;
const where = (facts: TemplateFacts): Text[] => [
  facts.venueName ? `${facts.venueName}${facts.venueAddress ? ` — ${facts.venueAddress}` : ''}` : blank('VENUE'),
];
const when = (session: SessionFact | undefined): Text[] =>
  session ? [`${session.date}${session.time ? `, ${session.time}` : ''}`] : [blank('DATE AND TIME')];

/** "Here's a look at the current leaders:" and the link — only once results are approved. */
function leaders(facts: TemplateFacts, intro: string): EmailBlock[] {
  if (!facts.resultsApproved || facts.standings.length === 0) return [];
  return [
    { kind: 'paragraph', text: [intro] },
    { kind: 'standings', title: 'Current leaders', rows: facts.standings, empty: 'LEADERS' },
    ...(facts.standingsUrl ? [{ kind: 'link' as const, label: 'See the full standings:', url: facts.standingsUrl }] : []),
  ];
}

/** Registration welcome — once per registration, or when the director invites a player. */
function confirmation(facts: TemplateFacts): EmailDocument {
  return {
    subject: `Welcome to ${facts.leagueName}`,
    heading: 'Welcome', league: facts.leagueName, subheading: facts.leagueName,
    greeting: { named: true },
    blocks: [
      { kind: 'paragraph', text: [`You’re registered for ${facts.leagueName}. We’re glad you’re joining us!`] },
      { kind: 'facts', rows: [{ label: 'Dates', lines: [facts.sessions.length ? dateList(facts.sessions) : blank('DATES')] }, { label: 'League director', lines: [directorOf(facts)] }] },
      { kind: 'setup' },
      ...(facts.playerUrl ? [{ kind: 'link' as const, label: 'Open your league:', url: facts.playerUrl }] : []),
      { kind: 'paragraph', text: ['Sign in to see your league and session details. We’ll send first-session information closer to the date.'] },
      { kind: 'note' },
    ],
    signOff: close(facts, 'See you at the tables!'),
  };
}

/** Kickoff — 9 AM, four days before session one. */
function welcome(facts: TemplateFacts): EmailDocument {
  const first = facts.sessions[0];
  return {
    subject: `Let’s play! ${facts.leagueName} starts ${first ? first.date : BLANK('DATE')}`,
    heading: 'Let’s play!',
    league: facts.leagueName,
    subheading: first ? `${facts.leagueName} · starts ${first.date}` : facts.leagueName,
    greeting: { named: false, text: 'Hi everyone!' },
    blocks: [
      { kind: 'paragraph', text: [`Our first ${facts.leagueName} session is almost here, and we’re excited to get together.`] },
      {
        kind: 'facts',
        rows: [
          { label: 'Where', lines: where(facts) },
          { label: 'When', lines: when(first) },
          { label: 'League director', lines: [directorOf(facts)] },
          { label: 'Bring', lines: ['Your NMJL card'] },
        ],
      },
      { kind: 'paragraph', text: ['Can’t make the first session? Report your absence in your league’s session page, or reply to your Director.'] },
      { kind: 'note' },
      { kind: 'setup' },
    ],
    signOff: close(facts, 'See you soon!'),
  };
}

/** The reminder — sessions two onward, with its own words for the last one. */
function reminder(facts: TemplateFacts): EmailDocument {
  const session = about(facts);
  const final = session !== undefined && session.number === facts.lastSession;
  const box: EmailBlock = {
    kind: 'facts',
    rows: [
      { label: 'When', lines: when(session) },
      { label: 'Where', lines: [facts.venueName ?? blank('VENUE')] },
    ],
  };
  if (final) {
    return {
      subject: `${facts.leagueName} | One more session to go!`,
      heading: 'One more session to go!',
      league: facts.leagueName,
      subheading: facts.leagueName,
      greeting: { named: false, text: 'Hi everyone!' },
      blocks: [
        { kind: 'paragraph', text: [`It’s hard to believe we’re already heading into our final ${facts.leagueName} session!`] },
        box,
        ...leaders(facts, 'Here’s a look at the current leaders heading into our final session:'),
        { kind: 'paragraph', text: ['We’ll celebrate this session’s winners and recognize our overall league winners once the final results are in.'] },
        { kind: 'paragraph', text: ['If you can’t attend, reply as soon as possible so we can coordinate a substitute.'] },
        { kind: 'note' },
      ],
      signOff: close(facts, 'Let’s finish with a great session together!'),
    };
  }
  return {
    subject: `${facts.leagueName} | See you on ${session ? session.date : BLANK('DATE')}!`,
    heading: session ? `See you on ${session.shortDate ?? session.date}!` : 'See you soon!',
    league: facts.leagueName,
    subheading: facts.leagueName,
    greeting: { named: false, text: 'Hi everyone!' },
    blocks: [
      { kind: 'paragraph', text: [`We’re looking forward to another ${facts.leagueName} session!`] },
      box,
      ...leaders(facts, 'Here’s a look at the current leaders:'),
      { kind: 'paragraph', text: ['We’ll have seating assignments ready, with another opportunity to play alongside different people.'] },
      { kind: 'paragraph', text: ['If you can’t make it, reply as soon as you know so we can arrange a substitute.'] },
      { kind: 'note' },
    ],
    signOff: close(facts, 'See you at the tables!'),
  };
}

/** Session celebration — every session but the last, once its scores are closed. */
function standings(facts: TemplateFacts): EmailDocument {
  const session = facts.sessions.find((s) => s.number === facts.aboutSession) ?? [...facts.sessions].reverse().find((s) => s.played);
  const next = after(facts, session);
  return {
    subject: `${facts.leagueName} | Session ${session ? session.number : BLANK('NUMBER')} results are in!`,
    heading: 'The results are in!',
    league: facts.leagueName,
    subheading: session ? `${facts.leagueName} · session ${session.number}` : facts.leagueName,
    greeting: { named: false, text: 'Hi everyone!' },
    blocks: [
      { kind: 'paragraph', text: [`Another ${facts.leagueName} session is in the books! Thanks for joining us and making the tables such a fun place to be.`] },
      ...(facts.lastSessionWinners.length
        ? [{ kind: 'paragraph' as const, text: [`Congratulations to our session winners! ${facts.lastSessionWinners.join(' and ')}`] }]
        : []),
      ...(facts.standingsUrl ? [{ kind: 'link' as const, label: 'See the latest standings:', url: facts.standingsUrl }] : []),
      { kind: 'paragraph', text: ['There’s still plenty of Mahjong ahead, and the standings can change from one session to the next.'] },
      ...(next ? [{ kind: 'paragraph' as const, text: [`We’re looking forward to seeing everyone again on ${next.date}.`] }] : []),
      { kind: 'note' },
    ],
    signOff: close(facts, 'See you at the tables!'),
  };
}

/** Final celebration — the last session's, in place of the ordinary one. */
function celebration(facts: TemplateFacts): EmailDocument {
  const overall = facts.overallWinners ?? [];
  return {
    subject: `That’s a wrap! Celebrating ${facts.leagueName}`,
    heading: 'That’s a wrap!',
    league: facts.leagueName,
    subheading: facts.leagueName,
    greeting: { named: false, text: 'Hi everyone!' },
    blocks: [
      { kind: 'paragraph', text: [`What a great series of Mahjong! Thank you for the friendly competition, good games, and community you helped build at ${facts.leagueName}.`] },
      ...(facts.lastSessionWinners.length
        ? [{ kind: 'paragraph' as const, text: [`Congratulations to our final-session winners! ${facts.lastSessionWinners.join(' and ')}`] }]
        : []),
      ...(overall.length
        ? [{ kind: 'paragraph' as const, text: [`And a special congratulations to our overall league winners! ${overall.join(' and ')}`] }]
        : []),
      ...(facts.standingsUrl && facts.resultsApproved ? [{ kind: 'link' as const, label: 'View the final standings:', url: facts.standingsUrl }] : []),
      { kind: 'paragraph', text: ['Whether you finished at the top of the leaderboard or came for a regular table and familiar faces, we’re glad you were part of it.'] },
      ...(facts.registrationUrl ? [{ kind: 'link' as const, label: 'Ready to join us again? Explore upcoming leagues:', url: facts.registrationUrl }] : []),
      { kind: 'note' },
    ],
    signOff: close(facts, 'Thanks for being part of this community. See you at the tables!'),
  };
}

export * from './schedule.js';
